import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge Context
const BadgeContext = createContext(null);

export const BadgeProvider = ({ children, panel }) => {
  const [badges, setBadges] = useState({});
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json'
  });

  // Badge sayılarını yükle
  const fetchBadges = useCallback(async () => {
    if (!panel) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/badges/counts`, {
        params: { panel },
        headers: getAuthHeaders()
      });
      setBadges(response.data || {});
    } catch (error) {
      console.error('Badge yükleme hatası:', error);
      setBadges({});
    } finally {
      setLoading(false);
    }
  }, [panel]);

  // Bölümü görüldü olarak işaretle
  const markSeen = useCallback(async (menu, tab = null, subTab = null) => {
    try {
      await axios.post(`${API_URL}/api/badges/mark-seen`, {
        panel,
        menu,
        tab,
        sub_tab: subTab
      }, { headers: getAuthHeaders() });
      
      // Local state'i güncelle
      setBadges(prev => {
        const updated = { ...prev };
        if (updated[menu]) {
          if (tab && updated[menu][tab] !== undefined) {
            updated[menu][tab] = 0;
          }
          // Total'i yeniden hesapla
          let total = 0;
          Object.entries(updated[menu]).forEach(([key, value]) => {
            if (key !== 'total' && typeof value === 'number') {
              total += value;
            }
          });
          updated[menu].total = total;
        }
        return updated;
      });
    } catch (error) {
      console.error('Mark seen hatası:', error);
    }
  }, [panel]);

  // Menü altındaki tümünü görüldü olarak işaretle
  const markAllSeen = useCallback(async (menu) => {
    try {
      await axios.post(`${API_URL}/api/badges/mark-all-seen`, null, {
        params: { panel, menu },
        headers: getAuthHeaders()
      });
      
      // Local state'i güncelle
      setBadges(prev => {
        const updated = { ...prev };
        if (updated[menu]) {
          Object.keys(updated[menu]).forEach(key => {
            updated[menu][key] = 0;
          });
        }
        return updated;
      });
    } catch (error) {
      console.error('Mark all seen hatası:', error);
    }
  }, [panel]);

  // Belirli bir menünün badge sayısını al
  const getBadgeCount = useCallback((menu, tab = null, subTab = null) => {
    if (!badges[menu]) return 0;
    
    if (subTab && badges[menu][subTab] !== undefined) {
      return badges[menu][subTab];
    }
    if (tab && badges[menu][tab] !== undefined) {
      return badges[menu][tab];
    }
    return badges[menu].total || 0;
  }, [badges]);

  // İlk yükleme
  useEffect(() => {
    fetchBadges();
    
    // Her 30 saniyede bir güncelle
    const interval = setInterval(fetchBadges, 30000);
    
    // Custom event dinle - badge güncellenmesi gerektiğinde
    const handleBadgeUpdate = () => {
      fetchBadges();
    };
    window.addEventListener('unread-counts-updated', handleBadgeUpdate);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('unread-counts-updated', handleBadgeUpdate);
    };
  }, [fetchBadges]);

  return (
    <BadgeContext.Provider value={{
      badges,
      loading,
      fetchBadges,
      markSeen,
      markAllSeen,
      getBadgeCount
    }}>
      {children}
    </BadgeContext.Provider>
  );
};

// Hook
export const useBadges = () => {
  const context = useContext(BadgeContext);
  if (!context) {
    // Context dışında kullanılırsa boş değerler döndür
    return {
      badges: {},
      loading: false,
      fetchBadges: () => {},
      markSeen: () => {},
      markAllSeen: () => {},
      getBadgeCount: () => 0
    };
  }
  return context;
};

// Badge bileşeni
export const BadgeCount = ({ count, className = "" }) => {
  if (!count || count <= 0) return null;
  
  return (
    <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse ${className}`}>
      {count > 99 ? '99+' : count}
    </span>
  );
};

export default useBadges;
